import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { responsiveFontSizes } from '@mui/material/styles';

const ThemeContext = createContext();

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeContextProvider');
  }
  return context;
};

const createAutobiaTheme = (mode) => {
  const baseTheme = createTheme({
    palette: {
      mode,
      ...(mode === 'light'
        ? {
            // Light mode colors
            primary: {
              light: "#FFF4EA",
              l700: "#FEF2E9",
              main: "#FA9940",
              dark: "#E27E30",
              medium: "#FDE5D2",
              contrastText: "#fff",
            },
            secondary: {
              light: "#E5E8FF",
              main: "#4A60F4",
              dark: "#495FA3",
              lightDisable: "#C1D8F0",
              contrastText: "#fff",
              s500: "#7C8CF9",
              s400: "#e9ebf9",
            },
            background: {
              default: "#fff",
              paper: "#fff",
              lightGray: "#f5f6fc",
            },
            text: {
              primary: "#344054",
              secondary: "#667085",
            },
            gray: {
              g25: "#F9FAFF",
              g25second: "#FCFCFD",
              g50: "#F9FAFB",
              g75: "#F4F5FA",
              g100: "#F2F4F7",
              g200: "#EAECF0",
              g400: "#98A2B3",
              g500: "#667085",
              g600: "#475467",
              g700: "#344054",
              background: "#FAFAFA",
              g60: "#EBEBEB",
            },
          }
        : {
            // Dark mode colors
            primary: {
              light: "#2D1810",
              l700: "#3D2118",
              main: "#FA9940",
              dark: "#FF9329",
              medium: "#2D1F14",
              contrastText: "#fff",
            },
            secondary: {
              light: "#1A1D2E",
              main: "#6B7BF7",
              dark: "#8A95F7",
              lightDisable: "#2A2F42",
              contrastText: "#fff",
              s500: "#9CA6FA",
              s400: "#2C2F3E",
            },
            background: {
              default: "#0A0B0F",
              paper: "#161821",
              lightGray: "#1E2028",
            },
            text: {
              primary: "#E4E7EC",
              secondary: "#98A2B3",
            },
            gray: {
              g25: "#0A0B0F",
              g25second: "#0F1014",
              g50: "#161821",
              g75: "#1E2028",
              g100: "#2C2F36",
              g200: "#3E4147",
              g400: "#6C727F",
              g500: "#98A2B3",
              g600: "#D0D5DD",
              g700: "#E4E7EC",
              background: "#0F1014",
              g60: "#3E4147",
            },
          }),
      // Colors that remain the same in both modes
      tertiary: {
        light: mode === 'light' ? "#EAECF0" : "#2C2F36",
        main: mode === 'light' ? "#000000" : "#FFFFFF",
        dark: mode === 'light' ? "#333232" : "#D0D5DD",
        contrastText: mode === 'light' ? "#fff" : "#000",
        tertiary2: mode === 'light' ? "#111827" : "#D0D5DD",
        tertiary3: mode === 'light' ? "#2B3449" : "#98A2B3",
      },
      success: {
        light: mode === 'light' ? "#ECFDF3" : "#0C2912",
        main: "#12B76A",
        dark: "#039855",
        contrastText: "#fff",
        s25: mode === 'light' ? "#F6FEF9" : "#0A1F0F",
        s50: mode === 'light' ? "#ECFDF3" : "#0C2912",
        s200: "#A6F4C5",
        s300: "#6CE9A6",
        s400: "#32D583",
        s500: "#12B76A",
        s600: "#039855",
        s900: "#054F31",
        s800: "#05603A",
      },
      error: {
        light: mode === 'light' ? "#FEF3F2" : "#2D0A0A",
        main: "#F04438",
        dark: "#D92D20",
        contrastText: "#fff",
        e400: "#F97066",
        e100: mode === 'light' ? "#FEE4E2" : "#2D1412",
        e50: mode === 'light' ? "#FEF3F2" : "#2D0A0A",
      },
      yellow: {
        light: mode === 'light' ? "#FFFBEB" : "#2D2608",
        main: "#FACC15",
        dark: "#EAB308",
      },
      white: mode === 'light' ? "#fff" : "#000",
      neutral: {
        shade1: mode === 'light' ? "#292929" : "#E4E7EC",
        shade2: mode === 'light' ? "#4B4B4B" : "#D0D5DD",
        shade3: mode === 'light' ? "#666666" : "#98A2B3",
        shade7: mode === 'light' ? "#F1F1F1" : "#2C2F36",
      },
      teal: {
        veryLight: mode === 'light' ? "#b2d8d8" : "#1A2E2E",
        light: mode === 'light' ? "#66b2b2" : "#1F3434",
        main: "#008080",
        dark: "#006666",
        veryDark: "#004c4c",
        grayTeal: mode === 'light' ? "#77909C" : "#5A6B75",
      },
      companies: {
        tabby: "#3BFFA9",
        tamara: "#F5AE84",
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        semiSm: 425,
        sm: 600,
        semiMd: 700,
        md: 900,
        semiLg: 1024,
        lg: 1200,
        xl: 1536,
        xxl: 1750,
        xxxl: 2000,
      },
    },
    typography: {
      fontFamily: "Almarai, Arial, sans-serif",
      regular: 400,
      medium: 500,
      bold: 700,
      h1: {
        fontSize: '2.5rem',
        fontWeight: 700,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 700,
        lineHeight: 1.3,
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 500,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: '1.125rem',
        fontWeight: 500,
        lineHeight: 1.4,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
      caption: {
        fontSize: '0.75rem',
        lineHeight: 1.4,
      },
    },
    shadows: [
      "none",
      mode === 'light' 
        ? "0px 2px 1px -1px rgba(173,174,176,0.2),0px 1px 1px 0px rgba(173,174,176,0.14),0px 1px 3px 0px rgba(173,174,176,0.12)"
        : "0px 2px 1px -1px rgba(0,0,0,0.3),0px 1px 1px 0px rgba(0,0,0,0.24),0px 1px 3px 0px rgba(0,0,0,0.20)",
      mode === 'light'
        ? "0px 3px 1px -2px rgba(173,174,176,0.2),0px 2px 2px 0px rgba(173,174,176,0.14),0px 1px 5px 0px rgba(173,174,176,0.12)"
        : "0px 3px 1px -2px rgba(0,0,0,0.3),0px 2px 2px 0px rgba(0,0,0,0.24),0px 1px 5px 0px rgba(0,0,0,0.20)",
      // Add more shadows as needed - keeping them shorter for brevity
      ...Array(22).fill(mode === 'light' 
        ? "0px 3px 3px -2px rgba(173,174,176,0.2),0px 3px 4px 0px rgba(173,174,176,0.14),0px 1px 8px 0px rgba(173,174,176,0.12)"
        : "0px 3px 3px -2px rgba(0,0,0,0.3),0px 3px 4px 0px rgba(0,0,0,0.24),0px 1px 8px 0px rgba(0,0,0,0.20)")
    ],
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'light' ? '#fff' : '#161821',
            color: mode === 'light' ? '#344054' : '#E4E7EC',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: mode === 'light' ? '#F9FAFF' : '#0F1014',
            borderColor: mode === 'light' ? '#EAECF0' : '#3E4147',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'light' ? '#fff' : '#161821',
          },
        },
      },
    },
  });

  return responsiveFontSizes(baseTheme);
};

export const ThemeContextProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    // Check localStorage first, then system preference
    const savedMode = localStorage.getItem('autobia-theme-mode');
    if (savedMode) return savedMode;
    
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  const toggleTheme = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('autobia-theme-mode', newMode);
  };

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually set a preference
      if (!localStorage.getItem('autobia-theme-mode')) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const theme = createAutobiaTheme(mode);

  const contextValue = {
    mode,
    toggleTheme,
    isDark: mode === 'dark',
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};